import type { IconName } from '@/components/ui/icon.js';

/**
 * Canonical list of in-house Originals — the single source of truth for which
 * games exist (matches game-registry.ts) and how they present (icon + gradient
 * + display name + in-game colour theme). Both the TopBar dropdown and the home
 * hero wheel render from this, so the two can never drift out of sync. Each
 * Original's real RTP is set in the backend (apps/game/src/originals/constants.ts
 * → RTP_BY_GAME) and shown on its card; third-party games never advertise an RTP.
 */
export interface OriginalEntry {
  /** Backend gameCode, the route :code, and the game-registry key. */
  code: string;
  name: string;
  icon: IconName;
  grad: string;
  theme: OriginalTheme;
  /**
   * Card-style art drawn for the game (scripts/art/originals-assets.ts), shipped under
   * public/originals/<code>/: `plate` paints the stage with its backdrop picture.
   */
  art?: { plate?: boolean };
}

/**
 * The colours a game's own page wears, lifted from its lobby tile so clicking
 * the tile lands somewhere that visibly continues it. The shell and the DOM
 * boards read these as CSS variables (see `originalThemeStyle` in theme.ts);
 * the canvas scenes read the same numbers through `originalTheme`.
 *
 * Win/loss colours (--success/--danger) are deliberately not here: they carry
 * meaning and stay the same in every game.
 */
export interface OriginalTheme {
  /** The tile's signature colour: the stage's glow and sunburst. */
  hue: string;
  /** Interactive accent: the bet button, selected chips, scene highlights. */
  accent: string;
  /** Text and icons drawn on `accent`; at least 4.5:1 against it. */
  accentOn: string;
  /** A light tint of the hue for glows, sparkles and pegs. */
  glow: string;
  /** The stage's darkest base. */
  deep: string;
  /** Tiles and wells drawn on the stage. */
  panel: string;
  /** The backdrop pattern the tile is painted with. */
  motif: 'sunburst' | 'streaks' | 'bubbles' | 'sparkle';
  /** Payout tiers lowest first, for games that colour outcomes by tier (the wheel). */
  tiers?: string[];
}

// Lobby gradients reference the brand ramp (--art-*, derived from --primary/--accent)
// so every Originals tile without art — home wheel, navbar dropdown, casino row —
// follows the tenant theme. Inside a game the tile's own colours take over.
export const ORIGINALS: OriginalEntry[] = [
  {
    code: 'wheel', name: 'Fortune Wheel', icon: 'rotate', grad: 'var(--art-1)',
    art: { plate: true },
    theme: {
      hue: '#d81b72', accent: '#d0218f', accentOn: '#ffffff', glow: '#ffc2e1',
      deep: '#2a0418', panel: '#561236', motif: 'sunburst',
      tiers: ['#3d1030', '#22d3ee', '#60a5fa', '#facc15', '#a855f7', '#fb923c', '#ff4fa3', '#fb7185', '#ffe27a'],
    },
  },
  {
    code: 'scratch', name: 'Lucky Scratch', icon: 'gift', grad: 'var(--art-6)',
    art: { plate: true },
    theme: {
      hue: '#169c45', accent: '#4cd137', accentOn: '#06230a', glow: '#cbffb4',
      deep: '#03200f', panel: '#0f4626', motif: 'sunburst',
    },
  },
  {
    code: 'cards', name: 'Card Flip', icon: 'spade', grad: 'var(--art-3)',
    art: { plate: true },
    theme: {
      hue: '#0c9a96', accent: '#5eead4', accentOn: '#032b27', glow: '#a9fff4',
      deep: '#022120', panel: '#0c4846', motif: 'streaks',
    },
  },
  {
    code: 'plinko', name: 'Plinko', icon: 'triangle', grad: 'var(--art-4)',
    art: { plate: true },
    theme: {
      hue: '#f47a12', accent: '#ff9a1f', accentOn: '#2a1000', glow: '#ffd79e',
      deep: '#2a1004', panel: '#4d2410', motif: 'sunburst',
    },
  },
  {
    code: 'dice', name: 'Thai Hi-Lo', icon: 'dice', grad: 'var(--art-7)',
    art: { plate: true },
    theme: {
      hue: '#c8102e', accent: '#f6c244', accentOn: '#2b1300', glow: '#ffd96b',
      deep: '#2a0509', panel: '#561421', motif: 'sunburst',
    },
  },
  {
    code: 'keno', name: 'Keno', icon: 'grid', grad: 'var(--art-2)',
    art: { plate: true },
    theme: {
      hue: '#0b9ee8', accent: '#38bdf8', accentOn: '#04213a', glow: '#a8efff',
      deep: '#072456', panel: '#14509e', motif: 'bubbles',
    },
  },
  {
    code: 'mines', name: 'Mines', icon: 'bomb', grad: 'var(--art-5)',
    art: { plate: true },
    theme: {
      hue: '#8a36c9', accent: '#9333ea', accentOn: '#ffffff', glow: '#f7d4ff',
      deep: '#170732', panel: '#272a63', motif: 'sparkle',
    },
  },
];

export const ORIGINALS_ART: Record<string, { icon: IconName; grad: string }> = Object.fromEntries(
  ORIGINALS.map((o) => [o.code, { icon: o.icon, grad: o.grad }]),
);
